'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StickerPublishSharePanel from './StickerPublishSharePanel';
import { buildLocateUrl, buildViewUrl } from '@/app/utils/shareLinks';
import { loadFarcasterSdk } from '@/app/utils/farcasterSdk';

type SharePreviewStatus = 'idle' | 'loading' | 'ready' | 'error';

type StickerSharePanelProps = {
  stickerId: number | null;
  stickerName?: string | null;
  isMiniApp?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
};

export default function StickerSharePanel({
  stickerId,
  stickerName,
  isMiniApp = false,
  isOpen = true,
  onClose,
}: StickerSharePanelProps) {
  const [sharePreviewStatus, setSharePreviewStatus] =
    useState<SharePreviewStatus>('idle');
  const [sharePreviewUrl, setSharePreviewUrl] = useState<string | null>(null);
  const [shareComposerBusy, setShareComposerBusy] = useState(false);
  const [shareCheckCount, setShareCheckCount] = useState(0);
  const [shareNextCheckIn, setShareNextCheckIn] = useState<number | null>(null);
  const [shareResourceUrl, setShareResourceUrl] = useState<string | null>(null);
  const [shareTimedOut, setShareTimedOut] = useState(false);
  const previewPollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewCountdownInterval = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const pollStartedAtRef = useRef<number | null>(null);
  const siteOrigin = useMemo(
    () => (typeof window !== 'undefined' ? window.location.origin : ''),
    [],
  );

  useEffect(() => {
    if (previewPollTimeout.current) {
      clearTimeout(previewPollTimeout.current);
      previewPollTimeout.current = null;
    }
    if (previewCountdownInterval.current) {
      clearInterval(previewCountdownInterval.current);
      previewCountdownInterval.current = null;
    }

    if (!stickerId || !isOpen) {
      setSharePreviewStatus('idle');
      setSharePreviewUrl(null);
      setShareCheckCount(0);
      setShareNextCheckIn(null);
      setShareResourceUrl(null);
      setShareTimedOut(false);
      pollStartedAtRef.current = null;
      return;
    }
    const resourceUrl = buildLocateUrl({
      resourceType: 'sticker',
      resourceId: stickerId,
      format: 'og',
    });
    if (!resourceUrl) {
      setSharePreviewStatus('error');
      setShareResourceUrl(null);
      return;
    }

    setSharePreviewStatus('loading');
    setSharePreviewUrl(null);
    setShareCheckCount(0);
    setShareTimedOut(false);
    pollStartedAtRef.current = Date.now();

    let isCancelled = false;
    setShareResourceUrl(resourceUrl);

    const scheduleNext = (delayMs: number) => {
      if (isCancelled) {
        return;
      }
      const startedAt = pollStartedAtRef.current;
      if (startedAt && Date.now() - startedAt >= 180000) {
        setSharePreviewStatus('error');
        setShareNextCheckIn(null);
        setShareTimedOut(true);
        if (previewPollTimeout.current) {
          clearTimeout(previewPollTimeout.current);
          previewPollTimeout.current = null;
        }
        if (previewCountdownInterval.current) {
          clearInterval(previewCountdownInterval.current);
          previewCountdownInterval.current = null;
        }
        return;
      }
      const delaySeconds = Math.max(1, Math.round(delayMs / 1000));
      setShareNextCheckIn(delaySeconds);
      if (previewCountdownInterval.current) {
        clearInterval(previewCountdownInterval.current);
      }
      previewCountdownInterval.current = setInterval(() => {
        setShareNextCheckIn(prev => {
          if (prev === null) {
            return prev;
          }
          if (prev <= 1) {
            if (previewCountdownInterval.current) {
              clearInterval(previewCountdownInterval.current);
              previewCountdownInterval.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      previewPollTimeout.current = setTimeout(poll, delayMs);
    };

    const poll = async () => {
      if (isCancelled) {
        return;
      }
      const startedAt = pollStartedAtRef.current;
      if (startedAt && Date.now() - startedAt >= 180000) {
        setSharePreviewStatus('error');
        setShareNextCheckIn(null);
        setShareTimedOut(true);
        if (previewPollTimeout.current) {
          clearTimeout(previewPollTimeout.current);
          previewPollTimeout.current = null;
        }
        if (previewCountdownInterval.current) {
          clearInterval(previewCountdownInterval.current);
          previewCountdownInterval.current = null;
        }
        return;
      }
      try {
        setShareCheckCount(prev => prev + 1);
        const response = await fetch(resourceUrl, { cache: 'no-store' });
        if (isCancelled) {
          return;
        }
        if (response.status === 202) {
          scheduleNext(5000);
          return;
        }
        if (response.ok) {
          setSharePreviewUrl(response.url);
          setSharePreviewStatus('ready');
          setShareNextCheckIn(null);
        } else {
          setSharePreviewStatus('error');
          if (response.status >= 500) {
            scheduleNext(12000);
          } else {
            scheduleNext(5000);
          }
        }
      } catch (_err) {
        if (!isCancelled) {
          scheduleNext(12000);
        }
      }
    };

    poll();

    return () => {
      isCancelled = true;
      if (previewPollTimeout.current) {
        clearTimeout(previewPollTimeout.current);
        previewPollTimeout.current = null;
      }
        if (previewCountdownInterval.current) {
          clearInterval(previewCountdownInterval.current);
          previewCountdownInterval.current = null;
        }
        pollStartedAtRef.current = null;
      };
    }, [stickerId, isOpen]);

  const buildShareMessage = useCallback(() => {
    if (!stickerId) {
      return null;
    }
    const shareUrl = buildViewUrl({
      resourceType: 'sticker',
      id: stickerId,
      siteUrl: siteOrigin || undefined,
    });
    const nameLabel = stickerName ? `"${stickerName}" ` : '';
    const message = `I just published ${nameLabel} on StickerChain. Slap it anywhere on planet Earth: ${shareUrl}`;
    return { message, shareUrl };
  }, [stickerId, stickerName, siteOrigin]);

  const handleShareStickerFarcaster = useCallback(() => {
    const shareData = buildShareMessage();
    if (!shareData) {
      return;
    }
    const { message, shareUrl } = shareData;

    if (isMiniApp) {
      console.log('Opening Farcaster share composer', {
        stickerId,
        stickerName,
        previewUrl: sharePreviewUrl,
        shareUrl,
        message,
      });
      setShareComposerBusy(true);
      void loadFarcasterSdk()
        .then((loadedSdk) => {
          const composeAction = loadedSdk?.actions?.composeCast;
          if (!composeAction) {
            return;
          }
          return composeAction({
            text: message,
            embeds: [shareUrl],
          });
        })
        .catch(err => {
          console.error('Unable to open Farcaster composer', err);
        })
        .finally(() => {
          setShareComposerBusy(false);
        });
      return;
    }

    const params = new URLSearchParams({ text: message });
    params.append('embeds[]', shareUrl);
    const shareTarget = `https://farcaster.xyz/~/compose?${params.toString()}`;
    window.open(shareTarget, '_blank', 'noopener,noreferrer');
  }, [buildShareMessage, isMiniApp, sharePreviewUrl, stickerId, stickerName]);

  const handleShareStickerX = useCallback(() => {
    const shareData = buildShareMessage();
    if (!shareData) {
      return;
    }
    const shareTarget = `https://x.com/intent/tweet?text=${encodeURIComponent(
      shareData.message,
    )}`;
    window.open(shareTarget, '_blank', 'noopener,noreferrer');
  }, [buildShareMessage]);

  if (!stickerId || !isOpen) {
    return null;
  }

  return (
    <StickerPublishSharePanel
      stickerId={stickerId}
      sharePreviewStatus={sharePreviewStatus}
      shareCheckCount={shareCheckCount}
      shareNextCheckIn={shareNextCheckIn}
      shareResourceUrl={shareResourceUrl}
      sharePreviewUrl={sharePreviewUrl}
      shareComposerBusy={shareComposerBusy}
      isMiniApp={isMiniApp}
      hasTimedOut={shareTimedOut}
      onShareX={handleShareStickerX}
      onShareFarcaster={handleShareStickerFarcaster}
      onClose={onClose}
    />
  );
}
